import Dropzone from 'react-dropzone';
import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import AddPhotoAlternateTwoToneIcon from '@mui/icons-material/AddPhotoAlternateTwoTone';
import Stack from '@mui/material/Stack';
import Button from '../../components/button';
import LinearProgressBar from '../../shared/material_components/linear-progress-bar';
import type { ProgressEvent } from './media-props';
import MetadataParser, { type MetadataInput } from './helpers/metadata-parser';

interface Props {
  buttonText: string,
  disabled?: boolean,
  onMediaAdd: (metadata: Object, uploadedFile: Object) => void,
  uploadFile: (file: File,
    progress: (event: ProgressEvent) => void) => Promise<Object>,
  fileTypes?: string,
}

const MediaUpload: React.FC<Props> = ({
  buttonText,
  disabled = false,
  onMediaAdd,
  uploadFile,
  fileTypes = '',
}: Props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const startUpload = async (file: File): Promise<Object | null> => {
    setUploading(true);
    const progressHandler = (e: ProgressEvent) : void => {
      if (e.lengthComputable) {
        setProgress(Number.parseInt(e.percent, 10));
      }
    };

    let uploadedFile = null;
    try {
      // @ts-ignore
      uploadedFile = await uploadFile(file, progressHandler);
    } catch {
      setErrorMessage('Upload failed. Please refresh and try again');
      return null;
    } finally {
      // Reset uploading state regardless of success/error
      setUploading(false);
      setProgress(0);
    }
    // @ts-ignore
    return uploadedFile;
  };

  const handleDrop = async (files: File[]) => {
    const uploadedFile = await startUpload(files[0]);

    if (!uploadedFile || Object.keys(uploadedFile).length === 0) {
      return;
    }
    const metadata = new MetadataParser().parse(uploadedFile as MetadataInput);

    onMediaAdd(metadata, uploadedFile);
  };

  return (
    <>
      {errorMessage
        && (
          <Alert
            severity="error"
            onClose={() => { setErrorMessage(''); }}
          >
            {errorMessage}
          </Alert>
        )}
      {uploading && (
        <Stack sx={{ color: '#4BAEF8' }}>
          <LinearProgressBar value={progress} color="inherit" />
        </Stack>
      )}
      {!disabled && (
        // @ts-ignore
        <Dropzone
          onDrop={handleDrop}
          disablePreview
          multiple={false}
          accept={fileTypes}
          style={{ width: '100%' }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddPhotoAlternateTwoToneIcon />}
          >
            {buttonText}
          </Button>
        </Dropzone>
      )}
    </>
  );
};

export default MediaUpload;
